import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CellRenderer } from './CellRenderer';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { columnWidth } from './columnWidths';
import type { SheetData } from './types';

interface VirtualizedTableProps {
  sheet: SheetData;
  /**
   * Height of the scroll viewport. Pass `'100%'` to fill a flex parent that
   * already has a definite height — the virtualizer needs the scroll element
   * to resolve to real pixels, which a `flex-1 min-h-0` parent guarantees.
   */
  height?: number | string;
  expanded?: boolean;
  /** Scale factor for row heights, column widths and font size (1 = default). */
  zoom?: number;
}

const BASE_ROW_HEIGHT = 24;
const BASE_HEADER_HEIGHT = 28;
const BASE_ROW_NUMBER_WIDTH = 48;
const BASE_FONT_SIZE = 12;

export function VirtualizedTable({
  sheet,
  height = 400,
  expanded = false,
  zoom = 1,
}: VirtualizedTableProps) {
  const gt = useGT();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowHeight = Math.round(BASE_ROW_HEIGHT * zoom);
  const headerHeight = Math.round(BASE_HEADER_HEIGHT * zoom);
  const rowNumberWidth = Math.round(BASE_ROW_NUMBER_WIDTH * zoom);
  const fontSize = BASE_FONT_SIZE * zoom;
  // Cell padding scales with zoom too: at 50% the header is only 14px tall,
  // so fixed `py-1.5`/`px-2` would swallow the whole cell. At zoom 1 these are
  // exactly Tailwind's px-2 (8px), py-1.5 (6px) and py-0.5 (2px).
  const headerCellPadding = `${6 * zoom}px ${8 * zoom}px`;
  const rowCellPadding = `${2 * zoom}px ${8 * zoom}px`;

  const rowVirtualizer = useVirtualizer({
    count: sheet.rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });

  // estimateSize is captured by the virtualizer; force a re-measure when the
  // zoom (and therefore the row height) changes so offsets stay in sync.
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, rowHeight]);

  // Widths the user dragged to, per sheet and column-width mode: switching
  // either starts from the fitted widths again, so Expand columns expands.
  const manualKey = `${sheet.name}\u0000${expanded}`;
  const [manual, setManual] = useState<{
    key: string;
    widths: Record<number, number>;
  }>({ key: manualKey, widths: {} });
  if (manual.key !== manualKey) setManual({ key: manualKey, widths: {} });

  // Each header's text measured as drawn, so Expand columns and a double-click
  // fit the real text, not the parser's per-character estimate.
  const headerText = useRef<(HTMLSpanElement | null)[]>([]);
  const [headerFit, setHeaderFit] = useState<number[]>([]);
  useLayoutEffect(() => {
    const measure = () => {
      const range = document.createRange();
      const measured = sheet.headers.map((_, col) => {
        const text = headerText.current[col];
        const cell = text?.parentElement;
        if (!text || !cell?.offsetWidth) return 0;
        // The text's true width, fractions included (scrollWidth rounds them
        // away and leaves "Regi…"), undoing any transform on the way (the
        // dialog opens scaled), then the cell's padding and its border.
        range.selectNodeContents(text);
        const scale = cell.getBoundingClientRect().width / cell.offsetWidth;
        const width = range.getBoundingClientRect().width / scale;
        return Math.ceil(width / zoom) + 16 + 1;
      });
      setHeaderFit((current) =>
        current.length === measured.length &&
        current.every((width, col) => width === measured[col])
          ? current
          : measured,
      );
    };
    measure();
    // The mono font may still be loading on the first measure.
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) measure();
    });
    return () => {
      live = false;
    };
  }, [sheet.headers, zoom, expanded]);

  if (sheet.rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        <T>No data in this sheet</T>
      </div>
    );
  }

  const columnWidths = sheet.headers.map((_, col) =>
    Math.round(
      columnWidth({
        manual: manual.widths[col],
        expanded,
        base: sheet.columnWidths[col] ?? 0,
        expandedEstimate: sheet.expandedColumnWidths[col] ?? 0,
        headerFit: headerFit[col],
      }) * zoom,
    ),
  );
  /** Widths are kept at zoom 1, so zooming scales a resized column too. */
  const resize = (col: number, drawn: number) =>
    setManual((current) => ({
      ...current,
      widths: { ...current.widths, [col]: drawn / zoom },
    }));
  const fit = (col: number) =>
    setManual((current) => ({
      ...current,
      widths: {
        ...current.widths,
        [col]: Math.max(
          sheet.expandedColumnWidths[col] ?? 0,
          headerFit[col] ?? 0,
        ),
      },
    }));
  const totalWidth =
    rowNumberWidth + columnWidths.reduce((sum, w) => sum + w, 0);

  return (
    <div
      ref={parentRef}
      role="grid"
      aria-label={sheet.name}
      aria-readonly
      // Rows and columns of the whole sheet, the header row and row numbers included.
      aria-rowcount={
        (sheet.truncated ? sheet.totalRows : sheet.rows.length) + 1
      }
      aria-colcount={sheet.headers.length + 1}
      tabIndex={0}
      className="overflow-auto focus-visible:outline-2 focus-visible:outline-ring"
      style={{ height }}
    >
      <div role="rowgroup" style={{ minWidth: totalWidth }}>
        <div
          role="row"
          aria-rowindex={1}
          className="flex sticky top-0 z-10 bg-muted font-mono border-b border-r"
          style={{ width: totalWidth, height: headerHeight, fontSize }}
        >
          <div
            role="columnheader"
            aria-colindex={1}
            aria-label={gt('Row number', {
              context: 'spreadsheet row number column',
            })}
            className="text-center text-muted-foreground border-r font-medium flex items-center justify-center shrink-0"
            style={{ width: rowNumberWidth, padding: headerCellPadding }}
          >
            #
          </div>
          {sheet.headers.map((header, colIndex) => {
            const isLast = colIndex === sheet.headers.length - 1;

            return (
              <Tooltip key={colIndex}>
                <TooltipTrigger asChild>
                  <div
                    role="columnheader"
                    aria-colindex={colIndex + 2}
                    // Named by its text alone, not its resize handle's label too.
                    aria-label={header}
                    title={header}
                    className={cn(
                      'relative font-medium flex items-center shrink-0 overflow-hidden whitespace-nowrap',
                      !expanded && 'text-ellipsis',
                      !isLast && 'border-r',
                    )}
                    style={{
                      width: columnWidths[colIndex],
                      padding: headerCellPadding,
                    }}
                  >
                    {/* Ellipsis needs a block box; the flex cell can't show one. */}
                    <span
                      ref={(element) => {
                        headerText.current[colIndex] = element;
                      }}
                      className="min-w-0 truncate"
                    >
                      {header}
                    </span>
                    <ColumnResizeHandle
                      header={header}
                      width={columnWidths[colIndex] ?? 0}
                      onResize={(drawn) => resize(colIndex, drawn)}
                      onFit={() => fit(colIndex)}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="font-mono text-xs">
                  {header}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = sheet.rows[virtualRow.index];
            return (
              <div
                key={virtualRow.index}
                role="row"
                aria-rowindex={virtualRow.index + 2}
                className={cn(
                  'flex absolute font-mono border-b border-r',
                  virtualRow.index % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                )}
                style={{
                  height: rowHeight,
                  transform: `translateY(${virtualRow.start}px)`,
                  width: totalWidth,
                  fontSize,
                }}
              >
                <div
                  role="rowheader"
                  aria-colindex={1}
                  className="text-center text-muted-foreground border-r bg-muted/30 flex items-center justify-center shrink-0"
                  style={{ width: rowNumberWidth, padding: rowCellPadding }}
                >
                  {virtualRow.index + 1}
                </div>
                {row.map((cell, colIndex) => {
                  const cellValue = cell.value;
                  const cellText = cellValue !== null ? String(cellValue) : '';
                  const colWidth = columnWidths[colIndex];
                  const needsTooltip =
                    !expanded &&
                    cellText.length * 7 * zoom > colWidth - 16 * zoom;
                  const isNumber = cell.type === 'number';
                  const isLast = colIndex === row.length - 1;

                  const cellDiv = (
                    <div
                      key={colIndex}
                      role="gridcell"
                      aria-colindex={colIndex + 2}
                      className={cn(
                        'shrink-0 overflow-hidden whitespace-nowrap',
                        !expanded && 'text-ellipsis',
                        !isLast && 'border-r',
                        isNumber && 'text-right',
                      )}
                      style={{ width: colWidth, padding: rowCellPadding }}
                    >
                      <CellRenderer cell={cell} />
                    </div>
                  );

                  if (needsTooltip) {
                    return (
                      <Tooltip key={colIndex}>
                        <TooltipTrigger asChild>{cellDiv}</TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-md whitespace-pre-wrap font-mono text-xs"
                        >
                          {cellText}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return cellDiv;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
